/* eslint-disable react/jsx-no-bind */
//@flow
import * as React from 'react';
import { themr } from 'react-css-themr';
import GridLayout from 'react-grid-layout';
import classnames from 'classnames';
import {
    Button,
    ButtonWithPopover,
    ButtonTypes
} from 'common/components/core/button';
import { dashboardColors, neoUIColors } from 'common/enums/ColorEnum';
import {
    PopoverTriggerEnum,
    PopoverPlaceEnum
} from 'common/components/core/popover/popOverFactory';
import Checkbox, { CheckboxTypes } from 'common/components/core/checkbox';
import { IconMenu, MenuItem, Menu } from 'common/components/core/menu';
import extend from 'lodash/extend';
import sortBy from 'lodash/sortBy';
import has from 'lodash/has';
import DataUtils from 'common/utils/DataUtils';
import { translate, Trans } from 'react-i18next';
import { OverflowDetectorPopover } from 'common/components/core/popover/OverflowDetectorPopover';
import ColumnSelectorTheme from 'common/components/core/table/ColumnSelector.scss';
import defaultTheme from './ActionsBar.scss';
import type { TFunction } from 'react-i18next';
import entityIds from 'common/tests/entity/entityIds.json';
import pjpPlansIds from 'common/tests/masterManagement/pjpPlans.json';
import { Text, Icon as NeoIcon, Tooltip, Box } from '@locus-taxy/neo-ui';
import Input, { InputTypes } from 'common/components/core/input';
import Icon from 'common/components/core/icon';

import {
    PermissionMatch,
    AccessDeniedMessage,
    type RoleOperationEnumType
} from 'common/modules/rbac';

import intelliSortIds from 'common/tests/intellisort/intelliSortIds.json';

type Props = {
    title?: string,
    entityNameId: string,
    selectedRows?: Array<any>,
    filteringEnabled?: boolean,
    disableColumnModifier?: boolean,
    filteredEntriesCount?: ?number,
    theme: Object,
    columns?: Array<Object>,
    onAdd?: () => void,
    onEdit?: ?() => void,
    onLocationEdit?: ?() => void,
    onBulkMarkAccurate?: ?() => void,
    onDelete?: ?() => void,
    disableAddWithText?: ?string,
    disableEditWithText?: ?string,
    disableDeleteWithText?: ?string,
    allFilteredEntitiesSelected: boolean,
    isLoading: boolean,
    pageSize: number,
    isBulkDeleteEnabled: boolean,
    enableFilteredEntitiesSelection?: boolean,
    resetSelectedRows: () => void,
    onSelectAllFilteredEntities: () => void,
    onImport: () => void,
    onColumnsChange?: (Array<Object>) => void,
    onEnableEntity: () => void,
    entityCanBeDisabled: ?boolean,
    t: TFunction,
    isEntityDisableInProgress: boolean,
    bulkResetPassword?: () => void,
    onResetPassword?: () => void,
    onResendPassword?: () => void,
    onResetQrCode?: () => void,
    onUnblockPersonnel?: () => void,
    onCreateBatch?: () => void,
    onUpdateOrder?: number => void,
    isResendPasswordInProgress: boolean,
    isResetPasswordInProgress: boolean,
    isResetQrCodeInProgress: boolean,
    operations: {
        add: RoleOperationEnumType,
        edit: RoleOperationEnumType,
        export: RoleOperationEnumType
    }
};

type State = {
    isColumnModifierMenuActive: boolean,
    searchInput: string
};

class ColumnCheckbox extends React.PureComponent {
    handleClick = (something, event) => {
        event.preventDefault();
        const { handleColumnSelect, columnKey, checked } = this.props;
        handleColumnSelect && handleColumnSelect(columnKey, !checked, event);
    };

    noOp = event => {
        event.preventDefault();
        event.stopPropagation();
    };

    render() {
        const { checked, label, theme, columnKey, hideDragger } = this.props;

        const labelWithTooltip = (
            <OverflowDetectorPopover
                invert
                className={theme.labelWrap}
                overflowText={label}
                popOverClassName={theme.labelPopover}
                preferPlace={PopoverPlaceEnum.Above}
                theme={theme}
            >
                {label.toUpperCase()}
            </OverflowDetectorPopover>
        );
        return (
            <div
                className={theme.cell}
                testId={`modifyColumnCheckbox_${columnKey}`}
            >
                <span
                    className={classnames(theme.dragger, {
                        [theme.hideDragger]: hideDragger
                    })}
                    onClick={this.noOp}
                >
                    =
                </span>
                <Checkbox
                    checkboxType={CheckboxTypes.Solid}
                    theme={theme}
                    label={labelWithTooltip}
                    checked={checked}
                    onChange={this.handleClick}
                    className={theme.draggableCheckbox}
                />
            </div>
        );
    }
}

const actionsBarFactory = ({ LeftComponent, RightComponent }) => {
    class ActionsBarComponent extends React.PureComponent<Props, State> {
        static isColumnsUpdated(columns, oldColumns) {
            return !columns.every((column, index) => {
                const columnId = column.id || column.accessor;
                const oldColumn = oldColumns[index];
                const oldColumnId = oldColumn.id || oldColumn.accessor;
                return columnId === oldColumnId;
            });
        }

        static sortColumnsByLayout(layouts, columns) {
            const sortedIds = sortBy(layouts, 'y').map(
                itemLayout => itemLayout.i
            );
            return sortBy(columns, column => {
                return !column.headerText
                    ? Infinity
                    : sortedIds.indexOf(column.id || column.accessor);
            });
        }

        static visibilityToggle(column, options) {
            const { id, accessor } = column;
            const { idToChange, visibility } = options;
            const columnId = id || accessor;
            if (columnId !== idToChange) {
                if (!has(column, 'show')) {
                    // HACK: react-table has this weird bug that if one of them is false,
                    // and others are not defined, instead of considering the default, it considers
                    // false for others too
                    return extend({}, column, { show: true });
                }
                return column;
            }
            return extend({}, column, { show: visibility });
        }

        static defaultProps = {
            allFilteredEntitiesSelected: false
        };

        state: State = {
            isColumnModifierMenuActive: false,
            searchInput: ''
        };
        showColumnModifierMenu = () => {
            this.setState({
                isColumnModifierMenuActive: true
            });
        };

        hideColumnModifierMenu = () => {
            this.setState({
                isColumnModifierMenuActive: false,
                searchInput: ''
            });
        };

        onModifyPinnedColumnsLayoutChange = layouts => {
            const {
                unpinnedColumns,
                pinnedColumns,
                noPinCols,
                onColumnsChange,
                columns: allColumns
            } = this.props;
            if (!allColumns) {
                throw new Error('columns not present');
            }
            const sortedColumns = ActionsBarComponent.sortColumnsByLayout(
                layouts,
                pinnedColumns
            );
            if (
                ActionsBarComponent.isColumnsUpdated(
                    sortedColumns,
                    pinnedColumns
                )
            ) {
                onColumnsChange &&
                    onColumnsChange({
                        columns: noPinCols.concat(
                            sortedColumns,
                            unpinnedColumns
                        ),
                        unpinnedColumns,
                        noPinCols,
                        pinnedColumns: sortedColumns
                    });
            }
        };
        onModifyUnpinnedColumnsLayoutChange = layouts => {
            const {
                unpinnedColumns,
                pinnedColumns,
                noPinCols,
                onColumnsChange,
                columns: allColumns
            } = this.props;
            if (!allColumns) {
                throw new Error('columns not present');
            }
            const sortedColumns = ActionsBarComponent.sortColumnsByLayout(
                layouts,
                unpinnedColumns
            );
            if (
                ActionsBarComponent.isColumnsUpdated(
                    sortedColumns,
                    unpinnedColumns
                )
            ) {
                onColumnsChange &&
                    onColumnsChange({
                        columns: noPinCols.concat(pinnedColumns, sortedColumns),
                        noPinCols,
                        pinnedColumns,
                        unpinnedColumns: sortedColumns
                    });
            }
        };

        onModifyColumnsLayoutChange = layouts => {
            const { columns, onColumnsChange } = this.props;
            if (!columns) {
                throw new Error('columns not present');
            }
            const sortedColumns = ActionsBarComponent.sortColumnsByLayout(
                layouts,
                columns
            );
            if (ActionsBarComponent.isColumnsUpdated(sortedColumns, columns)) {
                onColumnsChange &&
                    onColumnsChange({
                        columns: sortedColumns
                    });
            }
        };

        handleColumnSelect = (idToChange: string, visibility: boolean) => {
            const {
                columns,
                onColumnsChange,
                unpinnedColumns: prevUnpinnedColumns,
                pinnedColumns: prevPinnedColumns,
                noPinCols: prevNoPinCols,
                hasFreezableColumns
            } = this.props;
            const options = { idToChange, visibility };
            if (!columns) {
                throw new Error('columns not present');
            }
            const modifiedColumns = columns.map(column =>
                ActionsBarComponent.visibilityToggle(column, options)
            );
            if (hasFreezableColumns) {
                const unpinnedColumns = prevUnpinnedColumns.map(column => {
                    return ActionsBarComponent.visibilityToggle(
                        column,
                        options
                    );
                });
                const pinnedColumns = prevPinnedColumns.map(column => {
                    return ActionsBarComponent.visibilityToggle(
                        column,
                        options
                    );
                });
                const noPinCols = prevNoPinCols.map(column => {
                    return ActionsBarComponent.visibilityToggle(
                        column,
                        options
                    );
                });
                onColumnsChange &&
                    onColumnsChange({
                        columns: modifiedColumns,
                        unpinnedColumns,
                        pinnedColumns,
                        noPinCols
                    });
            } else {
                onColumnsChange &&
                    onColumnsChange({ columns: modifiedColumns });
            }
        };

        resetColumns = () => {
            const {
                columns,
                onColumnsChange,
                unpinnedColumns: prevUnpinnedColumns,
                pinnedColumns: prevPinnedColumns,
                noPinCols: prevNoPinCols,
                hasFreezableColumns
            } = this.props;
            if (!columns) {
                throw new Error('columns not present');
            }
            const modifiedColumns = columns.map(column => {
                return { ...column, show: true };
            });
            if (hasFreezableColumns) {
                const unpinnedColumns = prevUnpinnedColumns.map(column => ({
                    ...column,
                    show: true
                }));
                const pinnedColumns = prevPinnedColumns.map(column => ({
                    ...column,
                    show: true
                }));
                const noPinCols = prevNoPinCols.map(column => ({
                    ...column,
                    show: true
                }));
                onColumnsChange &&
                    onColumnsChange({
                        columns: modifiedColumns,
                        unpinnedColumns,
                        pinnedColumns,
                        noPinCols
                    });
            } else {
                onColumnsChange &&
                    onColumnsChange({ columns: modifiedColumns });
            }
        };

        handleSearchInputChange = (searchInput: string) => {
            this.setState({ searchInput });
        };

        getColumns(pinned: boolean) {
            const { columns, t, hasFreezableColumns } = this.props;
            const { searchInput } = this.state;

            if (!hasFreezableColumns && pinned) {
                return null;
            }

            const columnsToBeRendered = (columns || []).reduce(
                (columns, column) => {
                    const { id, accessor, headerText } = column;
                    if (
                        typeof column.headerText === 'string' &&
                        (pinned ? column.frozen : !column.frozen) &&
                        (!searchInput ||
                            column.headerText
                                .toLowerCase()
                                .includes(searchInput.toLowerCase()))
                    ) {
                        const menuItem = (
                            <MenuItem
                                theme={ColumnSelectorTheme}
                                ripple={false}
                                key={id || accessor}
                            >
                                <ColumnCheckbox
                                    columnKey={id || accessor}
                                    theme={ColumnSelectorTheme}
                                    label={headerText}
                                    checked={
                                        !has(column, 'show') || column.show
                                    }
                                    handleColumnSelect={this.handleColumnSelect}
                                    hideDragger={!!searchInput}
                                />
                            </MenuItem>
                        );
                        return [...columns, menuItem];
                    }
                    return columns;
                },
                []
            );

            const translationString = pinned ? 'pinnedColumn' : 'allColumn';

            let onModifyhandler;

            if (hasFreezableColumns) {
                onModifyhandler = pinned
                    ? this.onModifyPinnedColumnsLayoutChange
                    : this.onModifyUnpinnedColumnsLayoutChange;
            } else {
                onModifyhandler = this.onModifyColumnsLayoutChange;
            }
            return columnsToBeRendered.length ? (
                <React.Fragment>
                    {hasFreezableColumns ? (
                        <div className={ColumnSelectorTheme.gridHeader}>
                            {columnsToBeRendered.length > 1
                                ? t(
                                      `entityManagement:${translationString}_plural`
                                  )
                                : t(`entityManagement:${translationString}`)}
                        </div>
                    ) : null}
                    <GridLayout
                        className={ColumnSelectorTheme.menuItemWrap}
                        cols={1}
                        rowHeight={40}
                        width={250}
                        margin={[0, 0]}
                        onDragStop={onModifyhandler}
                        draggableHandle={`.${ColumnSelectorTheme.dragger}`}
                        isResizable={false}
                        enableUserSelectHack={false}
                    >
                        {columnsToBeRendered}
                    </GridLayout>
                </React.Fragment>
            ) : null;
        }

        getColumnList = () => {
            const pinnedColumns = this.getColumns(true);
            const unPinnedColumns = this.getColumns(false);
            const { t } = this.props;
            const { searchInput } = this.state;
            if (searchInput && !pinnedColumns && !unPinnedColumns) {
                return (
                    <div className={ColumnSelectorTheme.noResultSection}>
                        {t(`noColumnsFound`)}
                    </div>
                );
            }
            return (
                <>
                    {pinnedColumns ? (
                        <div testId={entityIds.menuItem.pinnedColumns}>
                            {pinnedColumns}
                        </div>
                    ) : null}
                    {unPinnedColumns ? (
                        <div testId={entityIds.menuItem.unPinnedColumns}>
                            {unPinnedColumns}
                        </div>
                    ) : null}
                </>
            );
        };

        renderColumnModifier() {
            const { columns, t } = this.props;
            const { isColumnModifierMenuActive, searchInput } = this.state;
            if (!columns) {
                return null;
            }
            return (
                <Menu
                    active={isColumnModifierMenuActive}
                    theme={ColumnSelectorTheme}
                    ripple={false}
                    position="topRight"
                    onHide={this.hideColumnModifierMenu}
                >
                    <div className={ColumnSelectorTheme.menuHeader}>
                        <div className={ColumnSelectorTheme.headerText}>
                            {t('selectColumns')}
                        </div>
                        <div className={ColumnSelectorTheme.headerRight}>
                            <Button
                                theme={ColumnSelectorTheme}
                                className={ColumnSelectorTheme.resetBtn}
                                ripple={false}
                                buttonType={ButtonTypes.Icon}
                                icon="reset"
                                onClick={this.resetColumns}
                                testId={entityIds.menuItem.reset}
                            />
                            <Button
                                ripple={false}
                                buttonType={ButtonTypes.Icon}
                                icon="cancel-thin"
                                onClick={this.hideColumnModifierMenu}
                                testId={entityIds.menuItem.close}
                            />
                        </div>
                    </div>
                    <div className={ColumnSelectorTheme.searchInputWrapper}>
                        {/* Hack to avoid glitch in action bar while loading master */}
                        {isColumnModifierMenuActive ? (
                            <Input
                                theme={ColumnSelectorTheme}
                                autoFocus
                                leftIcon={
                                    <Icon
                                        theme={ColumnSelectorTheme}
                                        className={
                                            ColumnSelectorTheme.searchIcon
                                        }
                                        icon="search"
                                    />
                                }
                                className={ColumnSelectorTheme.searchInputField}
                                inputType={InputTypes.NoBorder}
                                value={searchInput}
                                onChange={this.handleSearchInputChange}
                                placeholder={t('searchColumns')}
                                testId={entityIds.menuItem.searchColumns}
                                clearableInput
                            />
                        ) : null}
                    </div>
                    {this.getColumnList()}
                </Menu>
            );
        }

        renderSelectedEntitiesCount() {
            const {
                selectedRows,
                entityNameId,
                t,
                allFilteredEntitiesSelected,
                filteredEntriesCount,
                pageSize,
                enableFilteredEntitiesSelection,
                resetSelectedRows,
                onSelectAllFilteredEntities,
                theme,
                isBulkDeleteEnabled
            } = this.props;
            const selectedCount = selectedRows && selectedRows.length;
            const entityNameForSelectedCount =
                selectedCount &&
                t(`entities:${entityNameId}`, {
                    count: selectedCount
                });
            if (
                selectedCount &&
                !isBulkDeleteEnabled &&
                (!enableFilteredEntitiesSelection ||
                    selectedCount === filteredEntriesCount ||
                    filteredEntriesCount <= pageSize)
            ) {
                return (
                    <Trans t={t} i18nKey="noOfEntitiesSelected">
                        <div
                            className={theme.selectedCountWrap}
                            testId={pjpPlansIds.outlet.selectionCount}
                        >
                            <span className={theme.selectedCountText}>
                                <span className={theme.selectedCount}>
                                    {{ selectedCount }}
                                </span>{' '}
                                {{ entityNameForSelectedCount }} selected.
                            </span>
                        </div>
                    </Trans>
                );
            }

            if (
                selectedCount &&
                selectedCount < filteredEntriesCount &&
                !allFilteredEntitiesSelected
            ) {
                return (
                    <Trans t={t} i18nKey="noOfEntitiesAllSelected">
                        <div className={theme.selectedCountWrap}>
                            <span className={theme.selectedCountText}>
                                <span className={theme.selectedCount}>
                                    {{ selectedCount }}
                                </span>{' '}
                                {{ entityNameForSelectedCount }} selected.{' '}
                                <span
                                    onClick={onSelectAllFilteredEntities}
                                    className={theme.selectAll}
                                >
                                    Select All{' '}
                                    <span className={theme.selectedCount}>
                                        {{ filteredEntriesCount }}
                                    </span>
                                </span>
                            </span>
                        </div>
                    </Trans>
                );
            }

            if (allFilteredEntitiesSelected) {
                return (
                    <Trans t={t} i18nKey="allEntitiesSelectedCount">
                        <div className={theme.selectedCountWrap}>
                            <span className={theme.selectedCountText}>
                                All{' '}
                                <span className={theme.selectedCount}>
                                    {{ filteredEntriesCount }}
                                </span>{' '}
                                {{ entityNameForSelectedCount }} selected.{' '}
                                <span
                                    onClick={resetSelectedRows}
                                    className={theme.clearBtn}
                                >
                                    Clear?
                                </span>
                            </span>
                        </div>
                    </Trans>
                );
            }

            return null;
        }

        onUpdateOrderStatus = () => {
            const { onUpdateOrder, filteredEntriesCount } = this.props;
            onUpdateOrder?.(filteredEntriesCount);
        };

        render() {
            const {
                t,
                title,
                entityNameId,
                disableColumnModifier,
                filteringEnabled,
                filteredEntriesCount,
                onAdd,
                onEdit,
                onLocationEdit,
                onBulkMarkAccurate,
                onDelete,
                onImport,
                onExport,
                selectedRows,
                disableAddWithText,
                disableEditWithText,
                disableDeleteWithText,
                bulkDisableEntity,
                isLoading,
                theme,
                onEnableEntity,
                entityCanBeDisabled,
                isEntityDisableInProgress,
                bulkResetPassword,
                onResetPassword,
                onResendPassword,
                onResetQrCode,
                onUnblockPersonnel,
                onCreateBatch,
                onUpdateOrder,
                isResendPasswordInProgress,
                isResetPasswordInProgress,
                isResetQrCodeInProgress,
                operations
            } = this.props;
            const bulkDisable =
                bulkDisableEntity && selectedRows && selectedRows.length > 1
                    ? true
                    : false;
            const isBulkResetPassword =
                bulkResetPassword && selectedRows?.length > 1;
            const shouldShowResetPassword =
                (onResetPassword && selectedRows && selectedRows.length) ||
                isBulkResetPassword;
            const shouldShowResendPassword =
                (onResendPassword && selectedRows && selectedRows.length) ||
                isBulkResetPassword;
            const shouldShowResetQrCode =
                (onResetQrCode && selectedRows && selectedRows.length) ||
                isBulkResetPassword;
            const deleteLabel = entityCanBeDisabled
                ? t('translations:disable')
                : t('translations:delete');
            const deleteIcon = entityCanBeDisabled ? 'busy' : 'delete-bin';
            return (
                <div className={theme.actions}>
                    <div className={theme.left}>
                        <div className={theme.titleWrap}>
                            <Text
                                className={theme.title}
                                data-test-id={`title-${entityNameId}`}
                            >
                                {title ||
                                    t('entityMasterTitle', {
                                        entityNameId
                                    })}
                            </Text>
                            {isLoading ? (
                                <div className={theme.visibleCount}>...</div>
                            ) : !DataUtils.isEmpty(filteredEntriesCount) ? (
                                <div className={theme.visibleCount}>
                                    {filteringEnabled
                                        ? t('filteredCount', {
                                              count: filteredEntriesCount
                                          })
                                        : t('totalCount', {
                                              count: filteredEntriesCount
                                          })}
                                </div>
                            ) : null}
                        </div>
                        <div className={theme.buttons}>
                            {onBulkMarkAccurate ? (
                                <ButtonWithPopover
                                    className={theme.btn}
                                    theme={theme}
                                    buttonType={ButtonTypes.Text}
                                    color={dashboardColors.darkSkyBlue.name}
                                    onClick={onBulkMarkAccurate}
                                    label={t(`intelliSort:markAllLabel`)}
                                    testId={intelliSortIds.markAll}
                                    semibold
                                    uppercase
                                />
                            ) : (
                                []
                            )}
                            {onLocationEdit ? (
                                <ButtonWithPopover
                                    className={theme.btn}
                                    theme={theme}
                                    buttonType={ButtonTypes.Text}
                                    color={dashboardColors.darkSkyBlue.name}
                                    onClick={onLocationEdit}
                                    icon="location-icon"
                                    label={t('openInMap')}
                                    semibold
                                    uppercase
                                    testId={entityIds.openInMap}
                                />
                            ) : (
                                []
                            )}
                            {onEdit ? (
                                <PermissionMatch
                                    operations={operations.edit}
                                    render={hasPermission => (
                                        <ButtonWithPopover
                                            popOverContent={
                                                disableEditWithText ||
                                                (!hasPermission ? (
                                                    <AccessDeniedMessage
                                                        action={t(
                                                            'translations:edit'
                                                        )}
                                                    />
                                                ) : null)
                                            }
                                            triggerPopoverOn={
                                                PopoverTriggerEnum.Hover
                                            }
                                            className={theme.btn}
                                            theme={theme}
                                            buttonType={ButtonTypes.Text}
                                            color={
                                                dashboardColors.darkSkyBlue.name
                                            }
                                            onClick={onEdit}
                                            icon={'edit-icon'}
                                            label={t('translations:edit')}
                                            disabled={
                                                !!disableEditWithText ||
                                                !hasPermission
                                            }
                                            semibold
                                            uppercase
                                            testId={entityIds.edit}
                                        />
                                    )}
                                />
                            ) : (
                                []
                            )}
                            {(onDelete &&
                                selectedRows &&
                                selectedRows.length) ||
                            bulkDisable ? (
                                <PermissionMatch
                                    operations={operations.edit}
                                    render={hasPermission => (
                                        <ButtonWithPopover
                                            className={theme.btn}
                                            theme={theme}
                                            icon={deleteIcon}
                                            label={deleteLabel}
                                            semibold
                                            uppercase
                                            buttonType={ButtonTypes.Text}
                                            color={
                                                dashboardColors.salmonTwo.name
                                            }
                                            onClick={onDelete}
                                            popOverContent={
                                                disableDeleteWithText ||
                                                (!hasPermission ? (
                                                    <AccessDeniedMessage
                                                        action={deleteLabel}
                                                    />
                                                ) : null)
                                            }
                                            triggerPopoverOn={
                                                PopoverTriggerEnum.Hover
                                            }
                                            testId={entityIds.delete}
                                            disabled={
                                                !hasPermission ||
                                                isEntityDisableInProgress
                                            }
                                        />
                                    )}
                                />
                            ) : (
                                []
                            )}
                            {shouldShowResetPassword && (
                                <PermissionMatch
                                    operations={operations.edit}
                                    render={hasPermission => (
                                        <ButtonWithPopover
                                            className={theme.btn}
                                            theme={theme}
                                            icon="lock-reset"
                                            label={t('resetPassword')}
                                            semibold
                                            uppercase
                                            buttonType={ButtonTypes.Text}
                                            color={
                                                dashboardColors.salmonTwo.name
                                            }
                                            onClick={onResetPassword}
                                            testId={entityIds.resetPassword}
                                            popOverContent={
                                                !hasPermission ? (
                                                    <AccessDeniedMessage
                                                        action={t(
                                                            'resetPassword'
                                                        )}
                                                    />
                                                ) : null
                                            }
                                            disabled={
                                                !hasPermission ||
                                                isResetPasswordInProgress
                                            }
                                            triggerPopoverOn={
                                                PopoverTriggerEnum.Hover
                                            }
                                        />
                                    )}
                                />
                            )}

                            {shouldShowResendPassword && (
                                <PermissionMatch
                                    operations={operations.edit}
                                    render={hasPermission => (
                                        <ButtonWithPopover
                                            className={theme.btn}
                                            theme={theme}
                                            icon="lock-resend"
                                            label={t('resendPassword')}
                                            semibold
                                            uppercase
                                            buttonType={ButtonTypes.Text}
                                            color={
                                                dashboardColors.salmonTwo.name
                                            }
                                            onClick={onResendPassword}
                                            testId={entityIds.resendPassword}
                                            popOverContent={
                                                !hasPermission ? (
                                                    <AccessDeniedMessage
                                                        action={t(
                                                            'resendPassword'
                                                        )}
                                                    />
                                                ) : null
                                            }
                                            disabled={
                                                !hasPermission ||
                                                isResendPasswordInProgress
                                            }
                                            triggerPopoverOn={
                                                PopoverTriggerEnum.Hover
                                            }
                                        />
                                    )}
                                />
                            )}

                            {shouldShowResetQrCode && (
                                <PermissionMatch
                                    operations={operations.edit}
                                    render={hasPermission => (
                                        <ButtonWithPopover
                                            className={theme.btn}
                                            icon={
                                                <NeoIcon
                                                    icon="qr_scan"
                                                    color="red.400"
                                                    size="small"
                                                />
                                            }
                                            theme={theme}
                                            label={t('resetQrCode')}
                                            semibold
                                            uppercase
                                            buttonType={ButtonTypes.Text}
                                            color={neoUIColors.red400.name}
                                            onClick={onResetQrCode}
                                            disabled={
                                                isResetQrCodeInProgress ||
                                                !hasPermission
                                            }
                                            popOverContent={
                                                !hasPermission ? (
                                                    <AccessDeniedMessage
                                                        action={t(
                                                            'resetQrCode'
                                                        )}
                                                    />
                                                ) : null
                                            }
                                            triggerPopoverOn={
                                                PopoverTriggerEnum.Hover
                                            }
                                        />
                                    )}
                                />
                            )}
                            {onUnblockPersonnel && (
                                <PermissionMatch
                                    operations={operations.edit}
                                    render={hasPermission => (
                                        <ButtonWithPopover
                                            className={theme.btn}
                                            theme={theme}
                                            icon="lock-resend"
                                            label={t('unblock')}
                                            semibold
                                            uppercase
                                            buttonType={ButtonTypes.Text}
                                            color={
                                                dashboardColors.salmonTwo.name
                                            }
                                            onClick={onUnblockPersonnel}
                                            testId={entityIds.unblockPersonnel}
                                            popOverContent={
                                                !hasPermission ? (
                                                    <AccessDeniedMessage
                                                        action={t('unblock')}
                                                    />
                                                ) : null
                                            }
                                            disabled={!hasPermission}
                                            triggerPopoverOn={
                                                PopoverTriggerEnum.Hover
                                            }
                                        />
                                    )}
                                />
                            )}
                            {onCreateBatch && (
                                <ButtonWithPopover
                                    className={theme.btn}
                                    theme={theme}
                                    icon={'plus-thin'}
                                    label={t('planOnDispatchIQ')}
                                    semibold
                                    uppercase
                                    buttonType={ButtonTypes.Text}
                                    color={neoUIColors.blue500.name}
                                    onClick={onCreateBatch}
                                    testId={entityIds.planOnDispatchIQ}
                                />
                            )}
                            {onUpdateOrder && (
                                <ButtonWithPopover
                                    className={theme.btn}
                                    theme={theme}
                                    icon={
                                        <NeoIcon
                                            icon="dashboard_quote_management"
                                            color="blue.600"
                                            size="small"
                                        />
                                    }
                                    label={t(
                                        `orchestrator:quotations.getQuote`
                                    )}
                                    semibold
                                    uppercase
                                    buttonType={ButtonTypes.Text}
                                    color={neoUIColors.blue600.name}
                                    onClick={this.onUpdateOrderStatus}
                                />
                            )}
                        </div>
                        {LeftComponent ? (
                            <LeftComponent {...this.props} />
                        ) : null}
                    </div>
                    <div className={theme.right}>
                        {this.renderSelectedEntitiesCount()}
                        {onAdd ? (
                            <PermissionMatch
                                operations={operations.add}
                                render={hasPermission => (
                                    <ButtonWithPopover
                                        popOverContent={
                                            disableAddWithText ||
                                            (!hasPermission ? (
                                                <AccessDeniedMessage
                                                    action={t('addEntity', {
                                                        entityNameId
                                                    })}
                                                />
                                            ) : null)
                                        }
                                        triggerPopoverOn={
                                            PopoverTriggerEnum.Hover
                                        }
                                        className={theme.btn}
                                        theme={theme}
                                        buttonType={ButtonTypes.Solid}
                                        color={dashboardColors.darkSkyBlue.name}
                                        onClick={onAdd}
                                        icon={'plus-thin'}
                                        label={t('addEntity', { entityNameId })}
                                        disabled={
                                            !!disableAddWithText ||
                                            !hasPermission
                                        }
                                        semibold
                                        uppercase
                                        testId={entityIds.addEntity}
                                    />
                                )}
                            />
                        ) : (
                            []
                        )}
                        {onImport ? (
                            <PermissionMatch
                                operations={operations.add}
                                render={hasPermission => (
                                    <ButtonWithPopover
                                        className={theme.btn}
                                        theme={theme}
                                        buttonType={ButtonTypes.Solid}
                                        color={dashboardColors.darkSkyBlue.name}
                                        onClick={onImport}
                                        icon={'file-upload'}
                                        label={t('entityImport.importSheet')}
                                        semibold
                                        uppercase
                                        testId={entityIds.import}
                                        disabled={!hasPermission}
                                        triggerPopoverOn={
                                            PopoverTriggerEnum.Hover
                                        }
                                        popOverContent={
                                            !hasPermission ? (
                                                <AccessDeniedMessage
                                                    action={t(
                                                        'entityImport.importSheet'
                                                    )}
                                                />
                                            ) : null
                                        }
                                    />
                                )}
                            />
                        ) : null}
                        {!disableColumnModifier ? (
                            <IconMenu
                                icon="ellipsis_v"
                                theme={theme}
                                className={theme.overflowMenu}
                                testId={entityIds.ellipsis}
                            >
                                <MenuItem
                                    value="customizeColumns"
                                    caption={t('modifyColumns')}
                                    onClick={this.showColumnModifierMenu}
                                    testId={entityIds.menuItem.modifyColumns}
                                />
                                {onExport ? (
                                    <PermissionMatch
                                        operations={operations.export}
                                        render={hasPermission => {
                                            const action = t('export');
                                            return (
                                                <Tooltip
                                                    tooltip={
                                                        !hasPermission ? (
                                                            <AccessDeniedMessage
                                                                action={action}
                                                            />
                                                        ) : null
                                                    }
                                                >
                                                    <Box>
                                                        <MenuItem
                                                            value="export"
                                                            caption={action}
                                                            onClick={onExport}
                                                            disabled={
                                                                !hasPermission
                                                            }
                                                            testId={
                                                                entityIds
                                                                    .menuItem
                                                                    .export
                                                            }
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            );
                                        }}
                                    />
                                ) : null}
                                {onEnableEntity ? (
                                    <PermissionMatch
                                        operations={operations.edit}
                                        render={hasPermission => {
                                            const action = t('enableEntity', {
                                                entityNameId
                                            });
                                            return (
                                                <Tooltip
                                                    tooltip={
                                                        !hasPermission ? (
                                                            <AccessDeniedMessage
                                                                action={action}
                                                            />
                                                        ) : null
                                                    }
                                                >
                                                    <Box>
                                                        <MenuItem
                                                            value="enable"
                                                            caption={action}
                                                            onClick={
                                                                onEnableEntity
                                                            }
                                                            disabled={
                                                                !hasPermission
                                                            }
                                                            testId={
                                                                entityIds
                                                                    .menuItem
                                                                    .enableEntity
                                                            }
                                                        />
                                                    </Box>
                                                </Tooltip>
                                            );
                                        }}
                                    />
                                ) : null}
                            </IconMenu>
                        ) : null}
                    </div>
                    <div className={theme.modifyColumnsWrap}>
                        {this.renderColumnModifier()}
                    </div>
                    {RightComponent ? <RightComponent {...this.props} /> : null}
                </div>
            );
        }
    }
    return themr(
        'ActionsBar',
        defaultTheme
    )(translate('entityManagement')(ActionsBarComponent));
};
const ActionsBar = actionsBarFactory({});
export { actionsBarFactory };
export default ActionsBar;
